// src/hooks/useProximityAlert.ts
// Hook a közelségi értesítések kezeléséhez

import { useState, useEffect, useCallback, useRef } from 'react';

interface NearbyShop {
    _id: string;
    name: string;
    address: string;
    distance: number;
}

interface UseProximityAlertOptions {
    maxDistance?: number; // méterben, alapértelmezett: 1000 (1km)
    checkInterval?: number; // milliszekundumban, alapértelmezett: 60000 (1 perc)
    enabled?: boolean;
}

interface UseProximityAlertReturn {
    nearbyShop: NearbyShop | null;
    alertOpen: boolean;
    closeAlert: () => void;
    dismissForToday: () => void;
    isChecking: boolean;
    lastChecked: Date | null;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// LocalStorage kulcs a dismissált boltokhoz
const DISMISSED_SHOPS_KEY = 'perkly_dismissed_proximity_alerts';

// Visszaadja a ma dismissált bolt ID-kat
const getDismissedShopsToday = (): Set<string> => {
    try {
        const stored = localStorage.getItem(DISMISSED_SHOPS_KEY);
        if (!stored) return new Set();

        const data = JSON.parse(stored);
        const today = new Date().toDateString();

        // Ha más napról van az adat, töröljük
        if (data.date !== today) {
            localStorage.removeItem(DISMISSED_SHOPS_KEY);
            return new Set();
        }

        return new Set(data.shopIds);
    } catch {
        return new Set();
    }
};

// Bolt hozzáadása a mai dismissált listához
const addToDismissedToday = (shopId: string): void => {
    try {
        const current = getDismissedShopsToday();
        current.add(shopId);

        localStorage.setItem(DISMISSED_SHOPS_KEY, JSON.stringify({
            date: new Date().toDateString(),
            shopIds: Array.from(current),
        }));
    } catch (e) {
        console.error('Error saving dismissed shop:', e);
    }
};

// Ellenőrzi, hogy a bolt ma már dismissálva volt-e
const isShopDismissedToday = (shopId: string): boolean => {
    return getDismissedShopsToday().has(shopId);
};

export const useProximityAlert = (
    options: UseProximityAlertOptions = {}
): UseProximityAlertReturn => {
    const {
        maxDistance = 1000, // 1km
        checkInterval = 60000, // 1 perc
        enabled = true,
    } = options;

    const [nearbyShop, setNearbyShop] = useState<NearbyShop | null>(null);
    const [alertOpen, setAlertOpen] = useState(false);
    const [isChecking, setIsChecking] = useState(false);
    const [lastChecked, setLastChecked] = useState<Date | null>(null);

    const watchIdRef = useRef<number | null>(null);
    const lastAlertedShopRef = useRef<string | null>(null);

    // Közeli boltok lekérése a backend-től
    const fetchNearbyShops = useCallback(async (lat: number, lng: number): Promise<NearbyShop[]> => {
        try {
            const response = await fetch(
                `${API_URL}/api/shops/nearby?lat=${lat}&lng=${lng}&maxDistance=${maxDistance}`
            );

            if (!response.ok) {
                throw new Error('Failed to fetch nearby shops');
            }

            const data = await response.json();
            console.log('Nearby shops response:', data); // Debug log

            // Backend struktúra: { shops: [], total: ... }
            if (data.shops && Array.isArray(data.shops)) {
                return data.shops;
            }
            return [];
        } catch (error) {
            console.error('Error fetching nearby shops:', error);
            return [];
        }
    }, [maxDistance]);

    // Közelségi ellenőrzés
    const checkProximity = useCallback(async (position: GeolocationPosition) => {
        if (!enabled) return;

        setIsChecking(true);
        const { latitude, longitude } = position.coords;

        try {
            const shops = await fetchNearbyShops(latitude, longitude);

            if (shops.length > 0) {
                // Legközelebbi bolt kiválasztása
                const closest = shops.reduce((prev, current) =>
                    prev.distance < current.distance ? prev : current
                );

                // Ellenőrizzük a session storage-ot
                const lastSessionAlert = sessionStorage.getItem('perkly_last_alert_shop');

                // Ha még nem jeleztünk erről a boltról ebben a session-ben (vagy navigálás előtt)
                if (
                    !isShopDismissedToday(closest._id) &&
                    lastSessionAlert !== closest._id
                ) {
                    setNearbyShop(closest);
                    setAlertOpen(true);

                    // Elmentjük session storage-ba is
                    sessionStorage.setItem('perkly_last_alert_shop', closest._id);
                    lastAlertedShopRef.current = closest._id;
                }
            } else {
                // Ha nincs bolt a közelben, töröljük a session-t, hogy ha visszamegyünk, újra jelezzen
                sessionStorage.removeItem('perkly_last_alert_shop');
            }

            setLastChecked(new Date());
        } finally {
            setIsChecking(false);
        }
    }, [enabled, fetchNearbyShops]);

    // Alert bezárása
    const closeAlert = useCallback(() => {
        setAlertOpen(false);
    }, []);

    // Ma ne jelezzen erről a boltról
    const dismissForToday = useCallback(() => {
        if (nearbyShop) {
            addToDismissedToday(nearbyShop._id);
        }
        setAlertOpen(false);
        setNearbyShop(null);
    }, [nearbyShop]);

    // Geolocation figyelés indítása
    useEffect(() => {
        if (!enabled || !navigator.geolocation) {
            return;
        }

        // Első ellenőrzés azonnal
        navigator.geolocation.getCurrentPosition(
            checkProximity,
            (error) => console.log('Geolocation error:', error.message),
            { enableHighAccuracy: false, timeout: 10000 }
        );

        // Periodikus ellenőrzés
        const intervalId = setInterval(() => {
            navigator.geolocation.getCurrentPosition(
                checkProximity,
                (error) => console.log('Geolocation error:', error.message),
                { enableHighAccuracy: false, timeout: 10000 }
            );
        }, checkInterval);

        // Cleanup - ref értékét lokális változóba másoljuk
        const currentWatchId = watchIdRef.current;

        return () => {
            clearInterval(intervalId);
            if (currentWatchId !== null) {
                navigator.geolocation.clearWatch(currentWatchId);
            }
        };
    }, [enabled, checkInterval, checkProximity]);

    return {
        nearbyShop,
        alertOpen,
        closeAlert,
        dismissForToday,
        isChecking,
        lastChecked,
    };
};

export default useProximityAlert;
